// Package Imports
import { createRoute, lazyRouteComponent } from '@tanstack/react-router'

// Core Imports
import PageSkeleton from '@/core/components/page-skeleton'
import RouteError from '@/core/components/route-error'
import { queryClient } from '@/core/lib/query-client'
import { getCode, isAxios } from '@/core/lib/api-error'
import { routeHasPermission } from '@/core/utils/auth-utils'

// External Module Imports
import { dashboardRoute } from '@/modules/dashboard/dashboard.routes'
import { categoryListQueryOptions } from '@/modules/category/category.hooks'
import { questionPoolListQueryOptions } from '@/modules/question-pool/question-pool.hooks'

// Module Imports
import BatchNotFound from '@/modules/question-generation/components/batch-not-found'
import {
  batchSearchSchema,
  newGenerationSearchSchema,
} from '@/modules/question-generation/question-generation.schemas'
import {
  batchDetailQueryOptions,
  batchListQueryOptions,
  batchQuestionsQueryOptions,
  llmOptionsQueryOptions,
} from '@/modules/question-generation/question-generation.hooks'

export const questionGenerationRoute = createRoute({
  path: '/question-generation',
  getParentRoute: () => dashboardRoute,
  errorComponent: ({ error, reset }) => (
    <RouteError error={error} reset={reset} />
  ),
})

export const batchListRoute = createRoute({
  getParentRoute: () => questionGenerationRoute,
  path: '/',
  validateSearch: batchSearchSchema,
  loaderDeps: ({ search }) => ({
    page: search.page ?? 1,
    query: search.query ?? '',
    kind: search.kind ?? 'ALL',
  }),
  beforeLoad: ({ preload }) =>
    routeHasPermission(
      preload,
      'generation.list',
      'Soru üretimine erişim izniniz yok.'
    ),
  loader: ({ deps }) =>
    queryClient.ensureQueryData(
      batchListQueryOptions(deps.page, deps.query, deps.kind)
    ),
  head: () => ({ meta: [{ title: 'Soru Üretimi' }] }),
  pendingComponent: () => <PageSkeleton />,
  pendingMs: 100,
  component: lazyRouteComponent(
    () => import('@/modules/question-generation/pages/list-batches.page')
  ),
})

export const newGenerationRoute = createRoute({
  getParentRoute: () => questionGenerationRoute,
  path: '/new',
  validateSearch: newGenerationSearchSchema,
  loaderDeps: ({ search }) => ({ from: search.from }),
  beforeLoad: ({ preload }) =>
    routeHasPermission(
      preload,
      'generation.create',
      'Soru üretmek için yetkiniz yok.'
    ),
  // "Kopyala" (from) verilmişse kaynak grubun detayını da hazırla → form ilk render'da
  // önceden dolu gelir (react-hook-form defaultValues yalnız mount'ta uygulanır).
  loader: async ({ deps }) => {
    await Promise.all([
      queryClient.ensureQueryData(categoryListQueryOptions()),
      queryClient.ensureQueryData(questionPoolListQueryOptions('ACTIVE')),
      ...(deps.from
        ? [queryClient.ensureQueryData(batchDetailQueryOptions(deps.from))]
        : []),
    ])
  },
  head: () => ({ meta: [{ title: 'Yeni Soru Üretimi' }] }),
  pendingComponent: () => <PageSkeleton />,
  pendingMs: 100,
  component: lazyRouteComponent(
    () => import('@/modules/question-generation/pages/new-generation.page')
  ),
})

export const editDraftRoute = createRoute({
  getParentRoute: () => questionGenerationRoute,
  // `$id/edit` — `$id` (inceleme) rotasından daha spesifik; düzenleme yalnız taslaklara
  // açıktır (sayfa + backend `kind != DRAFT` → reddeder). İzin: üretim oluşturma yetkisi.
  path: '$id/edit',
  beforeLoad: ({ preload }) =>
    routeHasPermission(
      preload,
      'generation.create',
      'Taslak düzenlemek için yetkiniz yok.'
    ),
  loader: async ({ params }) => {
    await Promise.all([
      queryClient.ensureQueryData(batchDetailQueryOptions(params.id)),
      queryClient.ensureQueryData(categoryListQueryOptions()),
      queryClient.ensureQueryData(questionPoolListQueryOptions('ACTIVE')),
      queryClient.ensureQueryData(llmOptionsQueryOptions()),
    ])
  },
  errorComponent: ({ error, reset }) => {
    const notFound =
      (isAxios(error) && error.response?.status === 404) ||
      getCode(error) === 'GENERATION_NOT_FOUND'
    return notFound ? (
      <BatchNotFound />
    ) : (
      <RouteError error={error} reset={reset} />
    )
  },
  head: () => ({ meta: [{ title: 'Taslağı Düzenle' }] }),
  pendingComponent: () => <PageSkeleton />,
  pendingMs: 100,
  component: lazyRouteComponent(
    () => import('@/modules/question-generation/pages/edit-draft.page')
  ),
})

export const batchReviewRoute = createRoute({
  getParentRoute: () => questionGenerationRoute,
  path: '$id',
  beforeLoad: ({ preload }) =>
    routeHasPermission(
      preload,
      'generation.list',
      'Üretim grubunu incelemek için yetkiniz yok.'
    ),
  loader: async ({ params }) => {
    await Promise.all([
      queryClient.ensureQueryData(batchDetailQueryOptions(params.id)),
      queryClient.ensureQueryData(batchQuestionsQueryOptions(params.id)),
    ])
  },
  // Silinmiş/var-olmayan batch (404 GENERATION_NOT_FOUND) → jenerik kırmızı hata
  // yerine zarif "bulunamadı"; diğer hatalar genel RouteError'a düşer.
  errorComponent: ({ error, reset }) => {
    const notFound =
      (isAxios(error) && error.response?.status === 404) ||
      getCode(error) === 'GENERATION_NOT_FOUND'
    return notFound ? (
      <BatchNotFound />
    ) : (
      <RouteError error={error} reset={reset} />
    )
  },
  head: () => ({ meta: [{ title: 'Üretim Grubu İnceleme' }] }),
  pendingComponent: () => <PageSkeleton />,
  pendingMs: 100,
  component: lazyRouteComponent(
    () => import('@/modules/question-generation/pages/batch-review.page')
  ),
})
